// Centralized content & configuration for the HANSERA website.
// HANSERA is the customer-facing brand of the legal operator "Nevex Venture UG (haftungsbeschränkt)".
// Edit contact details, services and legal info here — no need to touch components.

import {
  Sparkles, Trees, LayoutPanelLeft, Hammer, Building2, Stethoscope, KeyRound, Users,
  Warehouse, MessageSquareText, FileCheck2, ShieldCheck, UserRound,
} from "lucide-react";

export const company = {
  brand: "HANSERA",
  tagline: "Gebäudeservice & Objektpflege",
  legalName: "Nevex Venture UG (haftungsbeschränkt)",
  brandLine: "HANSERA – eine Marke der Nevex Venture UG (haftungsbeschränkt)",
  managingDirector: "Necmettin Caliskan",
  location: "Bremen, Deutschland",
  serviceArea: "Bremen und Umgebung",
  domain: "hansera.de",
  positioning: "Ein zuverlässiger Ansprechpartner für Reinigung, Objektpflege und Ausbau.",
};

// Contact details — centrally configurable.
// The phone number may later be replaced by a professional telephone-service number.
// Leave `whatsapp` empty ("") to hide all WhatsApp options until a valid number exists.
export const contact = {
  phone: "+49 176 30536309",
  phoneHref: "tel:+4917630536309",
  email: "info@hansera.de",
  whatsapp: "", // e.g. "4917630536309" (no +, no spaces) — empty hides WhatsApp
  address: "Schleiermacherstraße 21, 28201 Bremen",
};

export const nav = [
  { label: "Startseite", href: "#start" },
  { label: "Leistungen", href: "#leistungen" },
  { label: "Über uns", href: "#ueber-uns" },
  { label: "Ablauf", href: "#ablauf" },
  { label: "Einsatzgebiet", href: "#einsatzgebiet" },
  { label: "Kontakt", href: "#kontakt" },
];

export const audiences = [
  { label: "Arztpraxen", icon: Stethoscope },
  { label: "Büros & Gewerbe", icon: Building2 },
  { label: "Hausverwaltungen", icon: KeyRound },
  { label: "Wohn- & Gewerbeobjekte", icon: Warehouse },
  { label: "Facility-Partner", icon: Users },
];

export const services = [
  {
    id: "reinigung",
    value: "Gebäudereinigung",
    number: "01",
    icon: Sparkles,
    title: "Gebäudereinigung & Reinigungsdienstleistungen",
    lead: "Unser Kernbereich: zuverlässige Reinigung für Arztpraxen, Büros, Gewerbe- und Wohnobjekte – als regelmäßige Unterhaltsreinigung oder als Einzelauftrag.",
    items: [
      "Unterhaltsreinigung",
      "Büroreinigung",
      "Praxisreinigung",
      "Treppenhausreinigung",
      "Bauendreinigung",
      "Ein- und Auszugsreinigung",
      "Glas- und Fensterreinigung",
    ],
    cta: "Kostenloses Angebot anfordern",
    featured: true,
    image: "https://images.unsplash.com/photo-1627905646269-7f034dcc5738?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Reinigungskraft mit Handschuhen reinigt einen Büroschreibtisch mit Sprühflasche und Tuch",
  },
  {
    id: "objektpflege",
    value: "Objekt- und Grundstückspflege",
    number: "02",
    icon: Trees,
    title: "Objekt- & Grundstückspflege",
    lead: "Gepflegte Außenanlagen und ordentliche Objekte – von der einmaligen Pflege bis zur regelmäßigen Betreuung nach Vereinbarung.",
    items: [
      "Objektpflege",
      "Rasenpflege",
      "Heckenschnitt",
      "Pflege von Grünflächen",
      "Laub- und Unkrautentfernung",
      "Reinigung von Wegen und Außenflächen",
      "Regelmäßige Pflege nach Vereinbarung",
    ],
    cta: "Kostenloses Angebot anfordern",
    featured: false,
    image: "https://images.unsplash.com/photo-1734079692079-172d8243ebd3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Fachkraft schneidet eine Hecke mit einer Heckenschere im Freien",
  },
  {
    id: "trockenbau",
    value: "Akustik- und Trockenbau",
    number: "03",
    icon: LayoutPanelLeft,
    title: "Akustik- & Trockenbauarbeiten",
    lead: "Zulassungsfreie Akustik- und Trockenbauarbeiten – saubere Lösungen für Wände, Decken und Räume im zulässigen Leistungsbereich.",
    items: [
      "Montage nichttragender Trennwände",
      "Deckenabhängungen",
      "Trockenbauverkleidungen",
      "Vorsatzschalen",
      "Dämm- und Schallschutzlösungen im zulässigen Leistungsbereich",
      "Vorbereitung von Räumen für den weiteren Innenausbau",
    ],
    cta: "Kostenloses Angebot anfordern",
    featured: false,
    image: "https://images.unsplash.com/photo-1587582423116-ec07293f0395?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Handwerker bei Trockenbauarbeiten an einer Innenwand",
  },
  {
    id: "innenausbau",
    value: "Innenausbau",
    number: "04",
    icon: Hammer,
    title: "Innenausbau",
    lead: "Koordinierte Innenausbauarbeiten nach vorheriger Prüfung des Projekts – der Leistungsumfang wird transparent im Angebot festgehalten.",
    items: [
      "Koordination und Ausführung geeigneter Innenausbauarbeiten",
      "Raumgestaltung und Modernisierung",
      "Wand- und Deckenlösungen",
      "Verkleidungs- und Montagearbeiten",
      "Vorbereitung und Ergänzung bestehender Innenräume",
      "Individuelle Arbeiten nach vorheriger Prüfung des Projekts",
    ],
    note: "Der konkrete Leistungsumfang wird vorab geprüft und transparent im Angebot festgehalten. Arbeiten außerhalb unseres eigenen Leistungsbereichs können bei Bedarf über geeignete Fachpartner abgestimmt werden.",
    cta: "Kostenloses Angebot anfordern",
    featured: false,
    image: "https://images.unsplash.com/photo-1649083048770-82e8ffd80431?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Modern gestalteter, frisch ausgebauter Wohnraum mit hellem Holzboden",
  },
];

export const benefits = [
  { icon: UserRound, title: "Persönlicher Ansprechpartner", text: "Direkte Kommunikation von der Anfrage bis zur laufenden Betreuung." },
  { icon: MessageSquareText, title: "Klare Leistungsabsprachen", text: "Leistungsumfang, Häufigkeit und Anforderungen werden vor Beginn eindeutig abgestimmt." },
  { icon: FileCheck2, title: "Angebote passend zum Objekt", text: "Keine unnötigen Pauschallösungen – das Angebot richtet sich nach Ihrem tatsächlichen Bedarf." },
  { icon: ShieldCheck, title: "Sorgfältige Ausführung", text: "Strukturierte Abläufe und ein klar definierter Leistungsumfang." },
  { icon: Users, title: "Flexible Betreuung", text: "Vom einmaligen Auftrag bis zur regelmäßigen Unterhaltsreinigung." },
];

export const processSteps = [
  { n: "1", title: "Anfrage senden", text: "Sie beschreiben uns die gewünschte Leistung." },
  { n: "2", title: "Details abstimmen", text: "Umfang, Ort, Zeitrahmen und besondere Anforderungen werden geklärt." },
  { n: "3", title: "Transparentes Angebot erhalten", text: "Der vereinbarte Leistungsumfang wird übersichtlich angeboten." },
  { n: "4", title: "Termin & Ausführung abstimmen", text: "Nach Beauftragung werden Termin und Durchführung abgestimmt." },
];

export const galleryCategories = ["Alle", "Reinigung", "Objektpflege", "Trockenbau", "Innenausbau"];

export const gallery = [
  { cat: "Reinigung", src: "https://images.unsplash.com/photo-1627905646269-7f034dcc5738?crop=entropy&cs=srgb&fm=jpg&q=80&w=900", alt: "Illustratives Servicebild Gebäudereinigung – Reinigung eines Büroschreibtisches" },
  { cat: "Reinigung", src: "https://images.unsplash.com/photo-1685602728726-76e0340cd1fc?crop=entropy&cs=srgb&fm=jpg&q=80&w=900", alt: "Illustratives Servicebild gereinigte Innenräume – kein reales HANSERA-Projekt" },
  { cat: "Objektpflege", src: "https://images.unsplash.com/photo-1641317430847-243b44c07125?crop=entropy&cs=srgb&fm=jpg&q=80&w=900", alt: "Illustratives Servicebild gepflegte Grünfläche – kein reales HANSERA-Projekt" },
  { cat: "Objektpflege", src: "https://images.unsplash.com/photo-1706920925080-be07c32398e1?crop=entropy&cs=srgb&fm=jpg&q=80&w=900", alt: "Illustratives Servicebild geschnittene Hecke – kein reales HANSERA-Projekt" },
  { cat: "Trockenbau", src: "https://images.unsplash.com/photo-1587582423116-ec07293f0395?crop=entropy&cs=srgb&fm=jpg&q=80&w=900", alt: "Illustratives Servicebild Trockenbauarbeiten – kein reales HANSERA-Projekt" },
  { cat: "Trockenbau", src: "https://images.unsplash.com/photo-1770048532712-4fde5ef7eb90?crop=entropy&cs=srgb&fm=jpg&q=80&w=900", alt: "Illustratives Servicebild Trennwand / Raumteiler – kein reales HANSERA-Projekt" },
  { cat: "Innenausbau", src: "https://images.unsplash.com/photo-1649083048770-82e8ffd80431?crop=entropy&cs=srgb&fm=jpg&q=80&w=900", alt: "Illustratives Servicebild ausgebauter Wohnraum – kein reales HANSERA-Projekt" },
  { cat: "Innenausbau", src: "https://images.unsplash.com/photo-1543525324-26e03b510586?crop=entropy&cs=srgb&fm=jpg&q=80&w=900", alt: "Illustratives Servicebild Innenausbau Detail – kein reales HANSERA-Projekt" },
];

export const faqs = [
  { q: "Welche Kunden betreuen Sie?", a: "Wir arbeiten schwerpunktmäßig für gewerbliche Auftraggeber wie Arztpraxen, Büros, Hausverwaltungen, Gewerbe- und Wohnobjekte sowie für Facility-Management-Partner. Passende Privatprojekte in Bremen und Umgebung sind ebenfalls möglich." },
  { q: "Sind auch regelmäßige Reinigungs- oder Pflegeaufträge möglich?", a: "Ja. Neben einmaligen Aufträgen lassen sich wiederkehrende Reinigungs- und Pflegeleistungen, etwa als Unterhaltsreinigung, nach Vereinbarung organisieren." },
  { q: "Wie erhalte ich ein Angebot?", a: "Beschreiben Sie uns über das Anfrageformular Ihr Vorhaben. Wir prüfen die Angaben, stimmen offene Punkte mit Ihnen ab und erstellen anschließend ein Angebot." },
  { q: "Ist vor dem Angebot eine Besichtigung erforderlich?", a: "Nicht immer. Bei größeren oder unklaren Vorhaben kann eine Besichtigung sinnvoll sein. Ob und wann eine Besichtigung notwendig ist, klären wir individuell mit Ihnen." },
  { q: "In welchen Gebieten sind Sie tätig?", a: "Wir bearbeiten Anfragen aus Bremen und dem näheren Umland. Ob ein Einsatz an Ihrem Standort möglich ist, klären wir gerne individuell." },
  { q: "Können mehrere Leistungen kombiniert werden?", a: "Ja. Reinigung, Objektpflege, Trockenbau und Innenausbau lassen sich – soweit sinnvoll – gemeinsam abstimmen und koordinieren." },
  { q: "Übernehmen Sie auch kleinere Aufträge?", a: "Auch kleinere Aufträge sind möglich. Beschreiben Sie uns einfach Ihr Anliegen, und wir prüfen die Umsetzung." },
  { q: "Wie schnell kann ein Auftrag beginnen?", a: "Der mögliche Beginn hängt von Art, Umfang, Ort und der aktuellen Kapazität ab. Wir stimmen einen realistischen Zeitrahmen gemeinsam mit Ihnen ab." },
];

export const customerTypes = ["Unternehmen / Büro", "Arztpraxis", "Hausverwaltung", "Eigentümer oder Vermieter", "Facility-Management / Partner", "Privatkunde"];
export const serviceOptions = ["Gebäudereinigung", "Objekt- und Grundstückspflege", "Akustik- und Trockenbau", "Innenausbau", "Mehrere Leistungen", "Sonstige Anfrage"];

// Legal information for Impressum / Datenschutz. HANSERA is a brand of the operator below.
export const legal = {
  brandLine: "HANSERA – eine Marke der Nevex Venture UG (haftungsbeschränkt)",
  companyName: "Nevex Venture UG (haftungsbeschränkt)",
  street: "Schleiermacherstraße 21",
  postalCity: "28201 Bremen",
  country: "Deutschland",
  managingDirector: "Necmettin Caliskan",
  registerCourt: "Amtsgericht Bremen",
  register: "HRB 41276 HB",
  vatId: "DE452860001",
  phone: "+49 176 30536309",
  email: "info@hansera.de",
};

export const seo = {
  title: "Gebäudereinigung Bremen | HANSERA Gebäudeservice",
  description: "HANSERA – Gebäudeservice & Objektpflege in Bremen: Unterhalts-, Büro-, Praxis- und Treppenhausreinigung sowie Bauendreinigung. Jetzt kostenlos anfragen.",
};
